import type { SettlementRowDto } from '../api/hr-admin';
import { getNumberLocale } from './base-i18n';

/**
 * 퇴사 정산 — 화면이 쓰는 순수 조각 (2026-08-30 · 판 J).
 *
 * ★ 급여에 넘기는 CSV 는 **보이는 표와 같은 값**이어야 한다. 화면과 파일이 다르면 어느 쪽이
 *   맞는지 물어야 하고, 그 물음이 정산을 멈춘다.
 */

export const RESIGN_KIND_LABEL: Readonly<Record<string, string>> = {
  VOLUNTARY: '자진 퇴사',
  CONTRACT_END: '계약 만료',
  DISMISSAL: '권고 · 해고',
  OTHER: '기타',
};

export const BASIS_LABEL: Readonly<Record<string, string>> = {
  COMPANY: '회사 규칙',
  STATUTORY: '입사일 기준',
};

/** 원 단위. 값이 없으면 «모름» 이다 — 0 으로 적으면 못 준 돈이 조용히 사라진다. */
export function wonText(v: number | null): string {
  // ⚠ 로케일을 글자로 박으면 검사가 막는다 — 언어 설정을 따르는 값을 쓴다.
  return v === null ? '임금 미등록' : `${v.toLocaleString(getNumberLocale())}원`;
}

/**
 * ★★ **두 기준이 갈렸는가.** 갈렸으면 화면이 그 사실을 말해야 한다 — 왜 이 숫자인지
 *    설명할 근거가 그것뿐이다.
 */
export function basisDiffers(r: SettlementRowDto): boolean {
  if (!r.company || !r.statutory) return false;
  return r.company.unused !== r.statutory.unused;
}

/** 급여로 넘길 표. BOM + CRLF — 윈도 엑셀이 한글을 안 깨뜨린다. */
export function settlementCsv(rows: readonly SettlementRowDto[]): string {
  const cell = (v: string | number): string => {
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = [
    '이름', '퇴사일', '퇴사 구분', '입사일',
    '회사기준 회차', '회사기준 부여', '회사기준 사용', '회사기준 미사용',
    '법정기준 회차', '법정기준 부여', '법정기준 사용', '법정기준 미사용',
    '적용 기준', '정산 일수', '보상휴가 잔여', '통상시급', '예상 수당',
  ];
  const body = rows.map((r) => [
    r.name, r.resignDate, RESIGN_KIND_LABEL[r.resignKind] ?? r.resignKind, r.hireDate ?? '',
    r.company ? `${r.company.periodStart}~${r.company.periodEnd}` : '',
    r.company?.granted ?? '', r.company?.used ?? '', r.company?.unused ?? '',
    r.statutory ? `${r.statutory.periodStart}~${r.statutory.periodEnd}` : '',
    r.statutory?.granted ?? '', r.statutory?.used ?? '', r.statutory?.unused ?? '',
    r.settleBasis ? BASIS_LABEL[r.settleBasis] ?? r.settleBasis : '',
    r.settleDays ?? '', r.substituteRemain,
    // ★ 모르는 값은 **빈 칸**으로 둔다. 0 을 적으면 급여가 「0원 지급」으로 읽는다.
    r.hourlyWage ?? '', r.estimatedPay ?? '',
  ]);
  return `﻿${[head, ...body].map((r) => r.map(cell).join(',')).join('\r\n')}\r\n`;
}
